/** Группы возможностей для карточек тарифов на лендинге и в кабинете владельца */

const GROUP_ORDER = [
  "club",
  "personalization",
  "finance",
  "communications",
  "social",
  "team",
  "analytics",
  "infrastructure",
  "support",
  "limits",
];

const GROUP_LABELS = {
  club: "Управление клубом",
  personalization: "Персонализация",
  finance: "Финансы",
  communications: "Коммуникации",
  social: "Сообщество",
  team: "Команда",
  analytics: "Аналитика",
  infrastructure: "Инфраструктура",
  support: "Поддержка",
  limits: "Лимиты",
};

const FEATURE_LABELS = {
  schedule: "Календарь и занятия",
  journal: "Журнал посещаемости",
  clients: "Ученики и родители",
  groups: "Группы и расписание",
  memberships: "Абонементы",
  client_portal: "Личный кабинет клиента (PWA)",
  club_life: "Лента клуба",
  competitions: "Соревнования",
  branding: "Полное оформление портала",
  branding_lite: "Только логотип клуба",
  white_label: "Родители не видят Бай.Спорт",
  splash_branding: "Свой splash-экран при запуске PWA",
  finance: "Учёт наличных и переводов",
  online_acquiring: "Онлайн-оплата картой (свои ключи)",
  push: "Push-уведомления",
  polls: "Базовые опросы",
  polls_advanced: "Опросы с триггерами",
  member_social: "Соцсеть участников",
  chat: "Чат с клиентами",
  trainer_portal: "Кабинет тренера",
  multi_trainer: "Несколько тренеров",
  admin_roles: "Администраторы с правами",
  analytics: "Аналитика и отчёты",
  sales_funnel: "Воронка продаж",
  analytics_export: "Выгрузка данных",
  multi_branch: "Несколько залов",
  priority_support: "Приоритетная поддержка",
};

function limitLabel(key, value) {
  if (value == null || value <= 0) return null;
  if (value >= 5000) return "Безлимит клиентов";
  switch (key) {
    case "max_trainers":
      return value === 1 ? "1 тренер (владелец)" : `До ${value} тренеров`;
    case "max_clients":
      return `До ${value} клиентов`;
    case "max_directions":
      return value >= 50 ? "Безлимит направлений" : value === 1 ? "1 направление" : `До ${value} направлений`;
    case "max_branches":
      return value > 1 ? `До ${value} залов в клубе` : null;
    case "max_clubs":
      return null;
    default:
      return null;
  }
}

export function buildPlanFeatureGroups(features = {}) {
  const groups = {};
  const f = features || {};

  for (const [key, label] of Object.entries(FEATURE_LABELS)) {
    if (!f[key]) continue;
    if (key === "branding_lite" && f.branding) continue;
    let category = "club";
    if (key.startsWith("max_")) category = "limits";
    else if (["branding", "branding_lite", "white_label", "splash_branding"].includes(key)) category = "personalization";
    else if (["finance", "online_acquiring"].includes(key)) category = "finance";
    else if (["push", "polls", "polls_advanced"].includes(key)) category = "communications";
    else if (["club_life", "member_social", "chat"].includes(key)) category = "social";
    else if (["trainer_portal", "multi_trainer", "admin_roles"].includes(key)) category = "team";
    else if (["analytics", "analytics_export", "sales_funnel"].includes(key)) category = "analytics";
    else if (key === "multi_branch") category = "infrastructure";
    else if (key === "priority_support") category = "support";

    if (!groups[category]) groups[category] = [];
    groups[category].push(label);
  }

  for (const key of ["max_trainers", "max_clients", "max_directions", "max_branches"]) {
    const text = limitLabel(key, f[key]);
    if (!text) continue;
    if (!groups.limits) groups.limits = [];
    if (!groups.limits.includes(text)) groups.limits.push(text);
  }

  return GROUP_ORDER.filter((id) => groups[id]?.length)
    .map((id) => ({
      id,
      title: GROUP_LABELS[id],
      items: groups[id],
    }));
}

export function planHighlightsFlat(features = {}, max = 7) {
  return buildPlanFeatureGroups(features)
    .flatMap((g) => g.items)
    .slice(0, max);
}

/** Короткая подпись позиционирования тарифа */
export const PLAN_POSITIONING = {
  basic: "1 тренер · без соцсети",
  pro: "Соцсеть и лента · 5 тренеров",
  business: "Максимум функций · без лимитов",
};

/** Канонические фичи для карточек лендинга (поверх API, если в БД устарело) */
export function mergePlanFeaturesForDisplay(planCode, apiFeatures = {}) {
  const canonical =
    LANDING_PLANS_FALLBACK.find((p) => p.code === planCode)?.features || {};
  return { ...apiFeatures, ...canonical };
}

const LANDING_SOCIAL_ROWS = [
  { key: "member_social", label: "Соцсеть участников" },
  { key: "club_life", label: "Лента клуба" },
  { key: "chat", label: "Чат с клиентами" },
];

function landingSocialItems(planCode, features = {}) {
  const f = mergePlanFeaturesForDisplay(planCode, features);
  if (planCode === "basic") {
    return f.chat ? [{ text: "Чат с клиентами", included: true }] : [];
  }
  return LANDING_SOCIAL_ROWS.map((row) => ({
    text: row.label,
    included: Boolean(f[row.key]),
  }));
}

const LANDING_PERSONALIZATION_ROWS = [
  { key: "branding", label: "Полное оформление портала" },
  { key: "white_label", label: "Родители не видят Бай.Спорт" },
  { key: "splash_branding", label: "Свой splash-экран при запуске PWA" },
];

function landingPersonalizationItems(planCode, features = {}) {
  const f = mergePlanFeaturesForDisplay(planCode, features);
  if (planCode !== "pro") return [];
  return LANDING_PERSONALIZATION_ROWS.filter((row) => f[row.key]).map((row) => ({
    text: row.label,
    included: true,
  }));
}

/** Группы для карточек тарифов на лендинге — с явным сравнением соцсети */
export function buildLandingPriceGroups(planCode, apiFeatures = {}) {
  const f = mergePlanFeaturesForDisplay(planCode, apiFeatures);
  const allGroups = buildPlanFeatureGroups(f);
  const limits = allGroups.find((g) => g.id === "limits");

  if (planCode === "business") {
    return [];
  }

  if (planCode === "basic" || planCode === "pro") {
    const groups = [
      {
        id: "social",
        title: GROUP_LABELS.social,
        items: landingSocialItems(planCode, apiFeatures),
      },
    ];
    const personalization = landingPersonalizationItems(planCode, apiFeatures);
    if (personalization.length) {
      groups.push({
        id: "personalization",
        title: GROUP_LABELS.personalization,
        items: personalization,
      });
    }
    if (limits) {
      groups.push({
        id: "limits",
        title: limits.title,
        items: limits.items.map((text) => ({ text, included: true })),
      });
    }
    return groups;
  }

  return allGroups.map((group) => ({
    ...group,
    items: group.items.map((text) => ({ text, included: true })),
  }));
}

/** Короткое описание «Максимального» — только отличие от «Расширенного» */
export function getBusinessPlanShortDescription() {
  return "Всё из «Расширенного» без жёстких лимитов по команде и участникам.";
}

/** Что есть только в «Максимальном» по сравнению с «Расширенным» */
export function listBusinessExclusiveFeatures(features = {}) {
  const f = mergePlanFeaturesForDisplay("business", features);
  const items = [];
  if (Number(f.max_trainers) > 5 || Number(f.max_clients) > 150) {
    items.push("Без жёстких лимитов по тренерам и участникам");
  }
  if (f.priority_support) {
    items.push("Приоритетная поддержка");
  }
  if (f.competitions) {
    items.push("Соревнования и расширенная аналитика");
  }
  return items;
}

/** Публичные тарифы для лендинга (fallback, если API недоступен) */
export const LANDING_PLANS_FALLBACK = [
  {
    id: "landing-basic",
    code: "basic",
    name: "Стартовый",
    description:
      "Журнал, абонементы, чат и PWA для одного клуба. Без соцсети участников и ленты.",
    monthly_price_rub: 590,
    yearly_price_rub: 5900,
    badge: null,
    features: {
      finance: true,
      polls: false,
      branding_lite: false,
      push: true,
      chat: true,
      sales_funnel: true,
      admin_roles: true,
      max_trainers: 1,
      max_clients: 25,
      max_directions: 2,
      max_branches: 1,
      max_clubs: 1,
    },
  },
  {
    id: "landing-pro",
    code: "pro",
    name: "Расширенный",
    description:
      "5 тренеров, 150 участников. Соцсеть, лента клуба, персонализация и онлайн-оплата — для одного клуба.",
    monthly_price_rub: 1190,
    yearly_price_rub: 11900,
    badge: "Популярный",
    features: {
      finance: true,
      polls: true,
      polls_advanced: true,
      branding: true,
      branding_lite: true,
      white_label: true,
      splash_branding: true,
      trainer_portal: true,
      multi_trainer: true,
      push: true,
      chat: true,
      club_life: true,
      member_social: true,
      competitions: true,
      online_acquiring: true,
      analytics: true,
      sales_funnel: true,
      admin_roles: true,
      max_trainers: 5,
      max_clients: 150,
      max_directions: 8,
      max_branches: 1,
      max_clubs: 2,
    },
  },
  {
    id: "landing-business",
    code: "business",
    name: "Максимальный",
    description:
      "Всё из «Расширенного» без жёстких лимитов — для крупной секции или сети залов в одном клубе.",
    monthly_price_rub: 1990,
    yearly_price_rub: 19900,
    badge: "Максимум",
    features: {
      finance: true,
      polls: true,
      polls_advanced: true,
      branding: true,
      branding_lite: true,
      white_label: true,
      splash_branding: true,
      trainer_portal: true,
      multi_trainer: true,
      push: true,
      chat: true,
      club_life: true,
      member_social: true,
      competitions: true,
      online_acquiring: true,
      analytics: true,
      sales_funnel: true,
      priority_support: true,
      admin_roles: true,
      analytics_export: false,
      max_trainers: 50,
      max_clients: 5000,
      max_directions: 50,
      max_branches: 1,
      max_clubs: 5,
    },
  },
];

export { GROUP_LABELS, FEATURE_LABELS };
